import { useState, useRef, useCallback } from 'react';
import { Bot, Upload, Clipboard, X, Save, Loader2, ImageIcon, Link, RefreshCw, Tag, Search } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import api from '../../utils/api';

// Konstanten für Dropdowns
const UNITS = ['Meter', 'Stueck', 'Rolle', 'kg', 'Liter', 'Paket', 'Set'];
const CATEGORIES = [
  'Kabel',
  'Leitungen',
  'Schalter',
  'Steckdosen',
  'Verteiler',
  'Beleuchtung',
  'Werkzeug',
  'Befestigung',
  'Rohre',
  'Sonstiges',
];

/**
 * K.I. Produkt-Assistent
 * Analyzes product screenshots using Vision LLM and extracts product data
 */
const ProductAIAssistant = ({ isOpen, onClose, onProductSaved }) => {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [productData, setProductData] = useState({
    productName: '',
    articleNumber: '',
    price: '',
    pricePerUnit: '',
    metalSurcharge: '',
    basePrice: '',
    unit: '',
    quantity: '',
    supplier: '',
    url: '',
    category: '',
    searchTerms: '',
  });
  const [hasAnalyzed, setHasAnalyzed] = useState(false);

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Helper: Ensure URL has protocol prefix
  const ensureUrlProtocol = (url) => {
    if (!url) return '';
    url = url.trim();
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      return 'https://' + url;
    }
    return url;
  };

  // Reset state when modal closes
  const handleClose = () => {
    setImage(null);
    setImagePreview(null);
    setProductData({
      productName: '',
      articleNumber: '',
      price: '',
      pricePerUnit: '',
      metalSurcharge: '',
      basePrice: '',
      unit: '',
      quantity: '',
      supplier: '',
      url: '',
      category: '',
      searchTerms: '',
    });
    setHasAnalyzed(false);
    setError(null);
    onClose();
  };

  // Handle file selection
  const handleFileSelect = useCallback((file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Nur Bildformate (PNG, JPG, WEBP, GIF) erlaubt');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('Bild zu groß (max. 10MB)');
      return;
    }

    setError(null);
    setImage(file);
    setHasAnalyzed(false);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle paste event (Ctrl+V)
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        handleFileSelect(file);
        break;
      }
    }
  }, [handleFileSelect]);

  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add('border-primary-500', 'bg-primary-50');
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('border-primary-500', 'bg-primary-50');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('border-primary-500', 'bg-primary-50');
    }

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Analyze image with AI
  const handleAnalyze = async () => {
    if (!image) {
      setError('Bitte zuerst ein Bild einfügen');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', image);

      const response = await api.post('/products/ai-analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.result) {
        const result = response.data.result;
        // Normalize unit to match dropdown options
        let normalizedUnit = result.unit || '';
        if (normalizedUnit) {
          const unitLower = normalizedUnit.toLowerCase();
          if (unitLower.includes('meter') || unitLower.includes('mtr') || unitLower === 'm') {
            normalizedUnit = 'Meter';
          } else if (unitLower.includes('stueck') || unitLower.includes('stück') || unitLower === 'stk') {
            normalizedUnit = 'Stueck';
          } else if (unitLower.includes('rolle')) {
            normalizedUnit = 'Rolle';
          } else if (unitLower.includes('paket') || unitLower.includes('pack')) {
            normalizedUnit = 'Paket';
          } else if (unitLower.includes('set')) {
            normalizedUnit = 'Set';
          } else if (unitLower.includes('liter') || unitLower === 'l') {
            normalizedUnit = 'Liter';
          } else if (unitLower === 'kg' || unitLower.includes('kilo')) {
            normalizedUnit = 'kg';
          }
        }
        // Normalize category
        let normalizedCategory = result.category || '';
        if (normalizedCategory && !CATEGORIES.includes(normalizedCategory)) {
          // Try to find a matching category
          const catLower = normalizedCategory.toLowerCase();
          normalizedCategory = CATEGORIES.find(c => c.toLowerCase() === catLower) || 'Sonstiges';
        }
        setProductData({
          productName: result.productName || '',
          articleNumber: result.articleNumber || '',
          price: result.price ? String(result.price) : '',
          pricePerUnit: result.pricePerUnit ? String(result.pricePerUnit) : '',
          metalSurcharge: result.metalSurcharge ? String(result.metalSurcharge) : '',
          basePrice: result.basePrice ? String(result.basePrice) : '',
          unit: normalizedUnit,
          quantity: result.quantity ? String(result.quantity) : '',
          supplier: result.supplier || '',
          url: ensureUrlProtocol(result.url),
          category: normalizedCategory,
          searchTerms: result.searchTerms || '',
        });
        setHasAnalyzed(true);
      } else {
        setError(response.data.error || 'K.I. konnte keine Daten extrahieren');
        // Set empty fields for manual entry
        setProductData({
          productName: '',
          articleNumber: '',
          price: '',
          pricePerUnit: '',
          metalSurcharge: '',
          basePrice: '',
          unit: '',
          quantity: '',
          supplier: '',
          url: '',
          category: '',
          searchTerms: '',
        });
        setHasAnalyzed(true);
      }
    } catch (err) {
      console.error('AI analysis error:', err);
      setError(err.response?.data?.error || 'Fehler bei der K.I. Analyse');
      setHasAnalyzed(true);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Save product
  const handleSave = async () => {
    if (!productData.productName.trim()) {
      setError('Produktname erforderlich');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('name', productData.productName.trim());
      formData.append('article_number', productData.articleNumber || '');
      formData.append('price', productData.price || '');
      formData.append('unit', productData.unit || '');
      formData.append('quantity', productData.quantity || '');
      formData.append('category', productData.category || '');
      formData.append('search_keywords', productData.searchTerms || '');

      // Build supplier links JSON
      const supplierUrl = ensureUrlProtocol(productData.url);
      if (productData.supplier && supplierUrl) {
        formData.append('supplier_links', JSON.stringify([{
          name: productData.supplier,
          url: supplierUrl,
        }]));
      }
      formData.append('supplier', productData.supplier || '');
      formData.append('supplier_link', supplierUrl);

      if (image) {
        formData.append('image', image);
      }

      const response = await api.post('/products/save-with-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        // Success!
        if (onProductSaved) {
          onProductSaved(response.data);
        }
        handleClose();
      }
    } catch (err) {
      console.error('Save product error:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  // Update product field
  const updateField = (field, value) => {
    setProductData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary-600" />
          <span>K.I. Produkt-Assistent</span>
        </div>
      }
      size="lg"
    >
      <div
        className="space-y-4"
        onPaste={handlePaste}
      >
        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Image upload area */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Screenshot einfuegen (Paste oder Upload):
          </label>

          <div
            ref={dropZoneRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors"
          >
            {imagePreview ? (
              <div className="space-y-3">
                <img
                  src={imagePreview}
                  alt="Screenshot Vorschau"
                  className="max-h-48 mx-auto rounded shadow"
                />
                <div className="flex justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImage(null);
                      setImagePreview(null);
                      setHasAnalyzed(false);
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Entfernen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-gray-500">
                <div className="flex justify-center gap-4">
                  <div className="flex flex-col items-center">
                    <Clipboard className="w-8 h-8 text-gray-400" />
                    <span className="text-sm">Ctrl+V</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-gray-400" />
                    <span className="text-sm">Datei wählen</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                    <span className="text-sm">Drag & Drop</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  PNG, JPG, WEBP oder GIF (max. 10MB)
                </p>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
            className="hidden"
          />
        </div>

        {/* Optional URL input */}
        {imagePreview && !hasAnalyzed && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Optional: URL zum Produkt
            </label>
            <Input
              type="url"
              placeholder="https://obeta.de/article-detail/..."
              value={productData.url}
              onChange={(e) => updateField('url', e.target.value)}
              icon={<Link className="w-4 h-4" />}
            />
          </div>
        )}

        {/* Analyze button */}
        {imagePreview && !hasAnalyzed && (
          <Button
            onClick={handleAnalyze}
            loading={isAnalyzing}
            className="w-full"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                K.I. analysiert...
              </>
            ) : (
              <>
                <Bot className="w-4 h-4 mr-2" />
                Analysieren
              </>
            )}
          </Button>
        )}

        {/* Results section */}
        {hasAnalyzed && (
          <div className="space-y-4 pt-2 border-t">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Ergebnis</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAnalyze}
                disabled={isAnalyzing}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${isAnalyzing ? 'animate-spin' : ''}`} />
                Neu analysieren
              </Button>
            </div>

            <p className="text-xs text-gray-500">
              Alle Felder sind editierbar!
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Zeile 1: Produktname */}
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Produktname *
                </label>
                <Input
                  value={productData.productName}
                  onChange={(e) => updateField('productName', e.target.value)}
                  placeholder="Produktname eingeben..."
                  required
                />
              </div>

              {/* Zeile 2: Artikelnummer, Preis, Menge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Artikelnummer
                </label>
                <Input
                  value={productData.articleNumber}
                  onChange={(e) => updateField('articleNumber', e.target.value)}
                  placeholder="z.B. 0106826"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gesamtpreis (EUR)
                </label>
                <Input
                  type="text"
                  value={productData.price}
                  onChange={(e) => updateField('price', e.target.value)}
                  placeholder="z.B. 493.45"
                />
                {/* Preis-Zusatzinfo wenn vorhanden */}
                {(productData.basePrice || productData.metalSurcharge || productData.pricePerUnit) && (
                  <p className="text-xs text-gray-500 mt-1">
                    ℹ️{' '}
                    {productData.basePrice && `Grundpreis: ${productData.basePrice} € `}
                    {productData.metalSurcharge && `| Metallzuschlag: ${productData.metalSurcharge} € `}
                    {productData.pricePerUnit && `| Pro ${productData.unit || 'Einheit'}: ${productData.pricePerUnit} €`}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Menge
                </label>
                <Input
                  type="number"
                  value={productData.quantity}
                  onChange={(e) => updateField('quantity', e.target.value)}
                  placeholder="z.B. 500"
                />
              </div>

              {/* Zeile 3: Einheit (Dropdown), Kategorie (Dropdown) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Einheit
                </label>
                <select
                  value={productData.unit}
                  onChange={(e) => updateField('unit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Auswählen...</option>
                  {UNITS.map((unit) => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Kategorie
                </label>
                <select
                  value={productData.category}
                  onChange={(e) => updateField('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Auswählen...</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Zeile 4: Lieferant und URL */}
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Link className="w-4 h-4 inline mr-1" />
                  Grosshaendler-Link
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input
                    value={productData.supplier}
                    onChange={(e) => updateField('supplier', e.target.value)}
                    placeholder="Lieferant (z.B. Obeta)"
                  />
                  <div className="md:col-span-2">
                    <Input
                      type="url"
                      value={productData.url}
                      onChange={(e) => updateField('url', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              {/* Zeile 5: Suchbegriffe */}
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Search className="w-4 h-4 inline mr-1" />
                  Suchbegriffe (komma-getrennt)
                </label>
                <Input
                  value={productData.searchTerms}
                  onChange={(e) => updateField('searchTerms', e.target.value)}
                  placeholder="NYM, Mantelleitung, 3x2.5, Kupferkabel, ..."
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer buttons */}
      {hasAnalyzed && (
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            disabled={!productData.productName.trim()}
          >
            <Save className="w-4 h-4 mr-2" />
            Als Produkt speichern
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default ProductAIAssistant;
